import { useCallback, useEffect, useState } from 'react';
import { installation as apiInstallation, onboarding } from '../api.js';
import type { AptitudeCible, CorpsInstallation, InstallationVue } from '../api.js';
import { BlocCommande } from '../components/BlocCommande.js';
import {
  attendDns,
  avancement,
  doitSonder,
  estReprenable,
  libelléDns,
} from './suivi-installation.js';
import type { StatutInstallation } from './suivi-installation.js';

/**
 * L'installation d'un site neuf, du formulaire à la mise en ligne.
 *
 * Composant à part, et non un bloc de plus dans l'assistant : ce qu'il montre est d'une autre
 * nature. Les autres étapes posent une question et reçoivent une réponse ; celle-ci lance un travail
 * qui dure une demi-heure, se suit, peut échouer et se reprendre, et se termine sur un geste à faire
 * ailleurs — chez un bureau d'enregistrement.
 *
 * Le sondage remplace l'attente. La version précédente lançait l'installation dans une requête
 * HTTP : le navigateur attendait cinquante minutes possibles, un rechargement de page perdait tout,
 * et le journal n'arrivait qu'à la fin — donc jamais en cas d'échec réseau.
 */

const INTERVALLE_SONDAGE_MS = 10_000;

export interface InstallationSiteNeufProps {
  organizationId: string;
  nom: string;
  url: string;
  aptitude: AptitudeCible | null;
  /** Installation déjà lancée, retrouvée à la reprise de l'assistant. */
  installationId: string | null;
  onInstallationLancée: (id: string) => void;
  onTerminée: () => void;
  onRetour: () => void;
}

/**
 * Un mot de passe fort, engendré **dans le navigateur**.
 *
 * Côté serveur, il ne pourrait être montré qu'une fois, dans un écran rafraîchi toutes les dix
 * secondes : il se perdrait au premier rechargement d'onglet. Et sans courriel configuré, Ghost
 * n'offre aucune réinitialisation — la personne serait enfermée dehors de son propre site.
 *
 * Ici, il est visible, modifiable, et reste sous les yeux tant que la page vit.
 */
function motDePasseEngendré(): string {
  const alphabet = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const octets = new Uint32Array(20);
  crypto.getRandomValues(octets);
  return [...octets].map((valeur) => alphabet[valeur % alphabet.length]).join('');
}

/**
 * Réglages SMTP pré-remplis : ceux du parc, pas une contrainte du logiciel.
 *
 * Tous les sites du parc expédient aujourd'hui par le service Mail d'Infomaniak, avec la même
 * adresse d'expédition. Les ressaisir à chaque installation n'apportait que des occasions de se
 * tromper — un `mail.infomaniak.com` mal orthographié ne se voit qu'au premier courriel qui ne part
 * pas, c'est-à-dire à la première réinitialisation de mot de passe, c'est-à-dire trop tard.
 *
 * Chaque champ reste modifiable : ce sont des valeurs de départ, et un site hébergé ailleurs se
 * configure en les remplaçant. Le mot de passe, lui, n'a pas de valeur par défaut et n'en aura
 * jamais — il s'engendre depuis le Service Mail, et un secret écrit dans le code du frontend serait
 * lisible par quiconque ouvre la page.
 */
const SMTP_PAR_DÉFAUT = {
  host: 'mail.infomaniak.com',
  // 587 en STARTTLS : le port de soumission recommandé par Infomaniak. 465 (TLS implicite) marche
  // aussi, 25 est bloqué en sortie chez la plupart des hébergeurs.
  port: '587',
  user: 'no-reply@socle.media',
  pass: '',
  from: 'no-reply@socle.media',
};

export default function InstallationSiteNeuf(props: InstallationSiteNeufProps) {
  const { organizationId } = props;

  const [version, setVersion] = useState('');
  const [www, setWww] = useState(true);
  const [nomPropriétaire, setNomPropriétaire] = useState('');
  const [courriel, setCourriel] = useState('');
  const [motDePasse, setMotDePasse] = useState(motDePasseEngendré);
  const [motDePasseSudo, setMotDePasseSudo] = useState('');
  const [smtp, setSmtp] = useState({ ...SMTP_PAR_DÉFAUT });
  const [avecCourriel, setAvecCourriel] = useState(false);

  const [suivi, setSuivi] = useState<InstallationVue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const idSuivi = props.installationId;

  /** Version proposée : la dernière publiée, jamais une valeur écrite en dur. */
  useEffect(() => {
    let annulé = false;
    void (async () => {
      const réponse = await onboarding.versionGhost(organizationId);
      if (annulé) return;
      if (réponse.status === 200 && réponse.data.version !== undefined) {
        setVersion(réponse.data.version);
      }
    })();
    return () => {
      annulé = true;
    };
  }, [organizationId]);

  const charger = useCallback(async () => {
    if (idSuivi === null) return;
    const réponse = await apiInstallation.suivre(organizationId, idSuivi);
    if (réponse.status === 200) setSuivi(réponse.data);
  }, [organizationId, idSuivi]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /**
   * Sondage régulier, tant qu'il se passe quelque chose.
   *
   * Y compris pendant l'attente du DNS : le serveur revérifie tout seul toutes les deux minutes, et
   * l'écran doit apprendre que le site est passé en ligne sans qu'on recharge.
   */
  const statut = (suivi?.status ?? 'en_file') as StatutInstallation;
  const àSonder = suivi !== null && doitSonder(statut);
  useEffect(() => {
    if (!àSonder) return;
    const minuterie = setInterval(() => void charger(), INTERVALLE_SONDAGE_MS);
    return () => clearInterval(minuterie);
  }, [àSonder, charger]);

  /*
   * La fin de l'installation fait avancer l'assistant.
   *
   * On ne dépend que du statut, volontairement : inclure la fonction de rappel relancerait l'effet
   * à chaque rendu du parent, et l'assistant sauterait en boucle sur son écran final.
   */
  const terminée = suivi?.status === 'terminee';
  const prévenir = props.onTerminée;
  useEffect(() => {
    if (terminée) prévenir();
  }, [terminée, prévenir]);

  const sudoRequis = props.aptitude?.privileges === 'sudo_mdp_requis';
  const privilègesAbsents = props.aptitude?.privileges === 'aucun';
  const systèmeRefusé = props.aptitude !== null && !props.aptitude.systeme.prisEnCharge;

  async function lancer(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      const corps: CorpsInstallation = {
        ghostVersion: version,
        www,
        owner: { name: nomPropriétaire, email: courriel, password: motDePasse },
        ...(motDePasseSudo === '' ? {} : { sudoPassword: motDePasseSudo }),
        ...(avecCourriel && smtp.host !== ''
          ? {
              courriel: {
                mail: {
                  transport: 'SMTP',
                  ...(smtp.from === '' ? {} : { from: smtp.from }),
                  options: {
                    host: smtp.host,
                    port: Number(smtp.port),
                    ...(smtp.user === '' ? {} : { auth: { user: smtp.user, pass: smtp.pass } }),
                  },
                },
              },
            }
          : {}),
      };
      const réponse = await apiInstallation.lancer(organizationId, corps);
      if (réponse.status !== 202 || réponse.data.installationId === undefined) {
        setErreur(réponse.data.error ?? 'Installation refusée');
        return;
      }
      props.onInstallationLancée(réponse.data.installationId);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(false);
    }
  }

  async function agir(action: 'reprendre' | 'verifier' | 'terminer'): Promise<void> {
    if (idSuivi === null) return;
    setErreur(null);
    const appel =
      action === 'reprendre'
        ? apiInstallation.reprendre
        : action === 'verifier'
          ? apiInstallation.vérifierDns
          : apiInstallation.terminer;
    const réponse = await appel(organizationId, idSuivi);
    if (réponse.status >= 400) setErreur(réponse.data.error ?? 'Action refusée');
    await charger();
  }

  // --- Le formulaire, avant tout lancement ----------------------------------------------------

  if (idSuivi === null) {
    return (
      <>
        <h3>Installer et mettre en ligne</h3>
        <p className="aide">
          Vigie pose Docker, un proxy TLS, Ghost et sa base, crée le compte propriétaire, et
          enregistre le site dans la flotte. À la fin, elle donne les enregistrements DNS à poser
          chez ton bureau d’enregistrement — la seule chose qu’elle ne peut pas faire à ta place.
        </p>
        <p className="avertissement">
          Le site sera installé sous le nom et l’adresse saisis à l’étape d’identité ({props.nom} —{' '}
          {props.url}). Un projet existant n’est jamais écrasé.
        </p>

        {systèmeRefusé && (
          <p className="erreur">
            {props.aptitude!.systeme.nom} n’est pas pris en charge : Vigie installe sur Debian 12 ou
            13 et Ubuntu 22.04, 24.04 ou 26.04.
          </p>
        )}
        {privilègesAbsents && (
          <p className="erreur">
            Ce compte ne peut pas élever ses privilèges, et l’installation de Docker en a besoin.
            Connecte-toi en root, ou avec un compte disposant de sudo.
          </p>
        )}
        {props.aptitude !== null && props.aptitude.reserves.length > 0 && (
          <ul className="liste-suite">
            {props.aptitude.reserves.map((réserve) => (
              <li key={réserve} className="avertissement">
                {réserve}
              </li>
            ))}
          </ul>
        )}

        <label htmlFor="version-ghost">Version de Ghost</label>
        <input
          id="version-ghost"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="6.0.0"
        />
        <p className="aide">
          Proposée : la dernière version stable publiée. Épinglée, jamais « latest » — c’est elle
          qui rend une mise à jour et un retour arrière possibles.
        </p>

        <label>
          <input type="checkbox" checked={www} onChange={(e) => setWww(e.target.checked)} />{' '}
          Rediriger www vers l’adresse du site
        </label>

        <h4>Compte propriétaire du site</h4>
        <p className="aide">
          Vigie le crée dans Ghost, et s’en sert pour obtenir la clé d’API qui lui permettra de
          superviser le site. Note ce mot de passe : il n’est ni conservé, ni réaffichable.
        </p>
        <label htmlFor="proprio-nom">Nom affiché</label>
        <input
          id="proprio-nom"
          value={nomPropriétaire}
          onChange={(e) => setNomPropriétaire(e.target.value)}
          placeholder="Rédaction"
        />
        <label htmlFor="proprio-courriel">Adresse de courriel</label>
        <input
          id="proprio-courriel"
          type="email"
          value={courriel}
          onChange={(e) => setCourriel(e.target.value)}
          placeholder="redaction@exemple.fr"
        />
        <label htmlFor="proprio-mdp">Mot de passe</label>
        <input
          id="proprio-mdp"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
        />
        <button type="button" className="lien" onClick={() => setMotDePasse(motDePasseEngendré())}>
          Engendrer un autre mot de passe
        </button>

        {sudoRequis && (
          <>
            <h4>Mot de passe sudo</h4>
            <p className="aide">
              Ce compte a sudo, mais sudo réclame un mot de passe. Vigie s’en sert le temps
              d’installer Docker, ne le transmet que par l’entrée standard — jamais sur une ligne de
              commande, que n’importe quel compte de la machine pourrait lire — et l’efface ensuite.
            </p>
            <input
              type="password"
              value={motDePasseSudo}
              onChange={(e) => setMotDePasseSudo(e.target.value)}
              placeholder="mot de passe sudo"
            />
          </>
        )}

        <h4>Courriel transactionnel (facultatif)</h4>
        <p className="aide">
          Sans lui, la vérification d’appareil de Ghost reste <strong>désactivée</strong> : elle
          enverrait un code par courriel que personne ne recevrait, et toute l’équipe serait
          enfermée dehors au premier changement de machine. Tu peux le configurer plus tard depuis
          l’éditeur de configuration, puis réactiver la vérification.
        </p>
        <label>
          <input
            type="checkbox"
            checked={avecCourriel}
            onChange={(e) => setAvecCourriel(e.target.checked)}
          />{' '}
          Configurer un serveur SMTP maintenant
        </label>
        {avecCourriel && (
          <div className="carte-interne">
            <label htmlFor="smtp-host">Serveur SMTP</label>
            <input
              id="smtp-host"
              value={smtp.host}
              onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
              placeholder="smtp.exemple.fr"
            />
            <label htmlFor="smtp-port">Port</label>
            <input
              id="smtp-port"
              value={smtp.port}
              onChange={(e) => setSmtp({ ...smtp, port: e.target.value })}
            />
            <p className="aide">
              587 en STARTTLS, recommandé. 465 convient aussi ; 25 est bloqué en sortie chez la
              plupart des hébergeurs.
            </p>
            <label htmlFor="smtp-from">Adresse d’expédition</label>
            <input
              id="smtp-from"
              value={smtp.from}
              onChange={(e) => setSmtp({ ...smtp, from: e.target.value })}
              placeholder="Rédaction <redaction@exemple.fr>"
            />
            <label htmlFor="smtp-user">Identifiant</label>
            <input
              id="smtp-user"
              value={smtp.user}
              onChange={(e) => setSmtp({ ...smtp, user: e.target.value })}
            />
            <label htmlFor="smtp-pass">Mot de passe</label>
            <input
              id="smtp-pass"
              type="password"
              value={smtp.pass}
              onChange={(e) => setSmtp({ ...smtp, pass: e.target.value })}
              placeholder="À générer depuis le Service Mail"
            />
            <p className="aide">
              À générer depuis Infomaniak › Service Mail ›{' '}
              {smtp.user === '' ? 'l’adresse' : smtp.user} › « Mot de passe d’application ». C’est
              le seul champ sans valeur par défaut.
            </p>
          </div>
        )}

        {erreur !== null && <p className="erreur">{erreur}</p>}
        <div className="actions">
          <button
            type="button"
            disabled={
              enCours ||
              version === '' ||
              nomPropriétaire === '' ||
              courriel === '' ||
              motDePasse.length < 10 ||
              systèmeRefusé ||
              privilègesAbsents ||
              (sudoRequis && motDePasseSudo === '')
            }
            onClick={() => void lancer()}
          >
            {enCours ? 'Lancement…' : 'Installer le site'}
          </button>
          <button type="button" className="lien" onClick={props.onRetour}>
            Revenir
          </button>
        </div>
      </>
    );
  }

  // --- Le suivi, après lancement --------------------------------------------------------------

  const étapes = avancement(statut);
  const contrôle = suivi?.dns?.dernierControle ?? null;

  return (
    <>
      <h3>Installation en cours</h3>
      <p className="aide">
        Une demi-heure environ : Docker à poser, trois images à télécharger, une base à créer. Tu
        peux fermer cet onglet — l’installation continue sur le serveur, et l’assistant la
        retrouvera.
      </p>

      <ol className="etapes">
        {étapes.map((étape) => (
          <li key={étape.clé} className={`etape etape--${étape.état}`}>
            <strong>{étape.libellé}</strong>
            <span className="aide"> {étape.détail}</span>
          </li>
        ))}
      </ol>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      {suivi !== null && suivi.avertissements.length > 0 && (
        <ul className="liste-suite">
          {suivi.avertissements.map((avertissement) => (
            <li key={avertissement} className="avertissement">
              {avertissement}
            </li>
          ))}
        </ul>
      )}

      {attendDns(statut) && suivi?.dns !== undefined && (
        <div className="carte-interne">
          <h4>Dernière étape : deux gestes, chez ton hébergeur</h4>
          <p className="aide">
            Ni l’un ni l’autre n’est à la portée de Vigie. Une fois les deux faits, le certificat
            s’obtient tout seul et le site est en ligne — Vigie revérifie toutes les deux minutes,
            sans que tu aies à revenir.
          </p>

          <h5>1. Les enregistrements DNS</h5>
          <p className="aide">À poser chez ton bureau d’enregistrement.</p>
          <BlocCommande où="à saisir dans la zone DNS du domaine" contenu={suivi.dns.texte} />

          <h5>2. Les ports 80 et 443</h5>
          <p className="aide">
            À ouvrir dans le groupe de sécurité de ton hébergeur. Sur un OpenStack comme Infomaniak
            Public Cloud, une instance neuve n’expose que le port 22 : sans le <strong>80</strong>,
            l’autorité de certification ne peut pas vérifier le domaine et aucun certificat ne sera
            émis ; sans le <strong>443</strong>, le site ne sera pas servi.
          </p>
          {contrôle?.portsPublics !== undefined && (
            <dl className="recapitulatif">
              <dt>Port 80</dt>
              <dd className={contrôle.portsPublics[80] ? 'succes' : 'erreur'}>
                {contrôle.portsPublics[80] ? 'répond' : 'ne répond pas'}
              </dd>
              <dt>Port 443</dt>
              <dd className={contrôle.portsPublics[443] ? 'succes' : 'erreur'}>
                {contrôle.portsPublics[443] ? 'répond' : 'ne répond pas'}
              </dd>
            </dl>
          )}
          <p className={contrôle?.verdict === 'correct' ? 'succes' : 'avertissement'}>
            {libelléDns(contrôle?.verdict)}
            {contrôle !== null &&
              ` (vérifié à ${new Date(contrôle.le).toLocaleTimeString('fr-FR')})`}
          </p>
          {/*
            Le message du dernier contrôle porte le diagnostic des ports, composé côté serveur —
            le seul endroit qui sait ce que la sonde a trouvé. L'afficher évite de le laisser
            dormir en base pendant que la personne cherche pourquoi son site ne monte pas.
          */}
          {contrôle?.portsPublics !== undefined && !contrôle.portsPublics[80] && (
            <p className="avertissement">{contrôle.message}</p>
          )}
          {contrôle?.certificatRelanceLe !== undefined && (
            <p className="succes">
              Les ports se sont ouverts : la bordure a été rechargée pour retenter le certificat
              immédiatement, sans attendre son prochain essai.
            </p>
          )}
          <p className="aide">
            La propagation prend de quelques minutes à quelques heures selon la durée de vie
            déclarée dans la zone.
          </p>
          <div className="actions actions--serrees">
            <button type="button" onClick={() => void agir('verifier')}>
              Revérifier maintenant
            </button>
            <button type="button" className="lien" onClick={() => void agir('terminer')}>
              Je gère le DNS moi-même, terminer
            </button>
          </div>
        </div>
      )}

      {estReprenable(statut) && (
        <div className="carte-interne">
          <h4>Installation arrêtée</h4>
          <p className="aide">
            Le journal ci-dessous dit où. Reprendre rejoue la séquence : ce qui est déjà fait n’est
            pas refait.
          </p>
          <div className="actions">
            <button type="button" onClick={() => void agir('reprendre')}>
              Reprendre
            </button>
          </div>
        </div>
      )}

      <details open={estReprenable(statut)}>
        <summary>Journal de l’installation</summary>
        <pre className="journal">{suivi?.log ?? ''}</pre>
      </details>
    </>
  );
}
